import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit3, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import {
  TaskyAlert,
  TaskyBadge,
  TaskyButton,
  TaskyEmptyState,
  TaskyFormGroup,
  TaskyInput,
  TaskySelect,
  TaskySpinner,
  TaskyTable,
  TaskyTableBody,
  TaskyTableCell,
  TaskyTableHead,
  TaskyTableHeaderCell,
  TaskyTableRow,
  TaskyTextarea,
} from '../tasky';
import axiosInstance from '../../services/api/axiosConfig';
import { useConfirm } from '../tasky/modal';

interface CarouselStyleType {
  id: number;
  category: string;
  name: string;
  description: string | null;
  prompt_template: string;
  sort_order: number;
  is_active: boolean;
}

interface CarouselStyleFormData {
  category: string;
  name: string;
  description: string;
  prompt_template: string;
  sort_order: number;
  is_active: boolean;
}

// 表示ラベルはユーザー向け CarouselStyleController の CATEGORY_LABELS と一致させる
const CATEGORY_OPTIONS = [
  { value: 'narrative', label: 'ストーリー設計' },
  { value: 'structure', label: '情報構成' },
  { value: 'promotion', label: '販促・CV' },
];

const defaultFormData: CarouselStyleFormData = {
  category: 'narrative',
  name: '',
  description: '',
  prompt_template: '',
  sort_order: 0,
  is_active: true,
};

const getCategoryLabel = (value: string): string =>
  CATEGORY_OPTIONS.find((opt) => opt.value === value)?.label || value;

const CarouselStyleManagement: React.FC = () => {
  const confirm = useConfirm();
  const [styles, setStyles] = useState<CarouselStyleType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<CarouselStyleFormData>(defaultFormData);
  const [error, setError] = useState<string | null>(null);

  const fetchStyles = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get('/admin/carousel-styles');
      if (response.data.success) {
        setStyles(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch carousel styles:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStyles();
  }, [fetchStyles]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      if (editingId) {
        await axiosInstance.put(`/admin/carousel-styles/${editingId}`, formData);
      } else {
        await axiosInstance.post('/admin/carousel-styles', formData);
      }
      setShowForm(false);
      setEditingId(null);
      setFormData(defaultFormData);
      fetchStyles();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save style');
    }
  };

  const handleEdit = (style: CarouselStyleType) => {
    setEditingId(style.id);
    setFormData({
      category: style.category,
      name: style.name,
      description: style.description || '',
      prompt_template: style.prompt_template,
      sort_order: style.sort_order,
      is_active: style.is_active,
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!(await confirm({ title: 'このスタイルを削除しますか？', tone: 'destructive', confirmText: '削除する' }))) return;
    try {
      await axiosInstance.delete(`/admin/carousel-styles/${id}`);
      fetchStyles();
    } catch (err) {
      console.error('Failed to delete style:', err);
    }
  };

  const handleToggle = async (id: number) => {
    try {
      await axiosInstance.put(`/admin/carousel-styles/${id}/toggle`);
      fetchStyles();
    } catch (err) {
      console.error('Failed to toggle style:', err);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData(defaultFormData);
    setError(null);
  };

  if (isLoading && styles.length === 0) {
    return (
      <div className="flex justify-center p-10">
        <TaskySpinner size="md" />
      </div>
    );
  }

  return (
    <div className="p-5">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-base font-semibold text-dark m-0">カルーセルスタイル管理</h3>
        <TaskyButton
          size="sm"
          onClick={() => { setShowForm(true); setEditingId(null); setFormData(defaultFormData); }}
        >
          <Plus size={16} /> 新規追加
        </TaskyButton>
      </div>

      {showForm && (
        <form
          onSubmit={handleSubmit}
          className="bg-warm-bg border border-warm-border rounded-tasky-md p-5 mb-5"
        >
          <h4 className="text-sm font-semibold text-dark mt-0 mb-4">
            {editingId ? 'スタイルを編集' : 'スタイルを追加'}
          </h4>
          {error && <TaskyAlert tone="error" className="mb-3">{error}</TaskyAlert>}

          <div className="grid gap-3">
            <div className="grid grid-cols-2 gap-3">
              <TaskyFormGroup label="カテゴリ" htmlFor="carousel-style-category">
                <TaskySelect
                  id="carousel-style-category"
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                >
                  {CATEGORY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </TaskySelect>
              </TaskyFormGroup>

              <TaskyFormGroup label="表示順" htmlFor="carousel-style-sort">
                <TaskyInput
                  id="carousel-style-sort"
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                />
              </TaskyFormGroup>
            </div>

            <TaskyFormGroup label="名前" htmlFor="carousel-style-name">
              <TaskyInput
                id="carousel-style-name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </TaskyFormGroup>

            <TaskyFormGroup label="説明" htmlFor="carousel-style-description">
              <TaskyTextarea
                id="carousel-style-description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
              />
            </TaskyFormGroup>

            <TaskyFormGroup label="プロンプトテンプレート" htmlFor="carousel-style-prompt">
              <TaskyTextarea
                id="carousel-style-prompt"
                value={formData.prompt_template}
                onChange={(e) => setFormData(prev => ({ ...prev, prompt_template: e.target.value }))}
                required
                rows={5}
              />
            </TaskyFormGroup>
          </div>

          <div className="flex gap-2 mt-4">
            <TaskyButton type="submit" size="sm">
              {editingId ? '更新' : '追加'}
            </TaskyButton>
            <TaskyButton type="button" variant="outline" size="sm" onClick={handleCancel}>
              キャンセル
            </TaskyButton>
          </div>
        </form>
      )}

      {styles.length > 0 ? (
        <TaskyTable>
          <TaskyTableHead>
            <tr>
              <TaskyTableHeaderCell>順序</TaskyTableHeaderCell>
              <TaskyTableHeaderCell>名前</TaskyTableHeaderCell>
              <TaskyTableHeaderCell>説明</TaskyTableHeaderCell>
              <TaskyTableHeaderCell>カテゴリ</TaskyTableHeaderCell>
              <TaskyTableHeaderCell align="center">状態</TaskyTableHeaderCell>
              <TaskyTableHeaderCell align="center">操作</TaskyTableHeaderCell>
            </tr>
          </TaskyTableHead>
          <TaskyTableBody>
            {styles.map(style => (
              <TaskyTableRow key={style.id}>
                <TaskyTableCell>{style.sort_order}</TaskyTableCell>
                <TaskyTableCell className="font-semibold">{style.name}</TaskyTableCell>
                <TaskyTableCell className="max-w-[300px] truncate text-gray-500">
                  {style.description}
                </TaskyTableCell>
                <TaskyTableCell>
                  <TaskyBadge variant="secondary">{getCategoryLabel(style.category)}</TaskyBadge>
                </TaskyTableCell>
                <TaskyTableCell align="center">
                  <button
                    type="button"
                    onClick={() => handleToggle(style.id)}
                    aria-label={style.is_active ? '無効にする' : '有効にする'}
                    className="bg-transparent border-none cursor-pointer p-0"
                  >
                    {style.is_active ? (
                      <ToggleRight size={24} className="text-success" />
                    ) : (
                      <ToggleLeft size={24} className="text-gray-400" />
                    )}
                  </button>
                </TaskyTableCell>
                <TaskyTableCell align="center">
                  <div className="flex items-center justify-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleEdit(style)}
                      aria-label="編集"
                      className="bg-transparent border-none cursor-pointer p-0 text-primary hover:text-primary-hover transition-colors"
                    >
                      <Edit3 size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(style.id)}
                      aria-label="削除"
                      className="bg-transparent border-none cursor-pointer p-0 text-destructive hover:text-destructive/80 transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </TaskyTableCell>
              </TaskyTableRow>
            ))}
          </TaskyTableBody>
        </TaskyTable>
      ) : (
        !isLoading && <TaskyEmptyState title="スタイルがありません" className="mt-4" />
      )}
    </div>
  );
};

export default CarouselStyleManagement;
